/**
 * @file core 的错误类型与「未实现」哨兵。
 *
 * 约定：core 只在**违反契约**时抛异常（编程错误、非法配置、内部不变量破裂）。
 * 玩家的非法操作**不抛异常**，而是返回 `action_rejected` 事件 —— 因为在
 * server-authoritative 模型下，非法操作是正常业务流量（作弊尝试、网络乱序、
 * UI 抢跑），抛异常会让 Durable Object 的请求处理路径变脆。
 */

/** 契约未实现。M1-T2 只定契约，所有导出函数体均为此错误。 */
export class NotImplementedError extends Error {
  override readonly name = 'NotImplementedError';
  constructor(symbol: string) {
    super(
      `[@majiang/core] "${symbol}" 尚未实现 —— 本函数在 M1-T2 阶段只定契约，实现见 M1-T3。`,
    );
  }
}

/**
 * 「未实现」哨兵。
 *
 * 参数 `args` 存在的唯一目的是消费函数入参，从而在
 * `noUnusedParameters: true` 下仍能保留有语义的参数名（而不是被迫写 `_state`）。
 * 实现落地后本文件的调用点会被逐一移除。
 *
 * @param symbol 未实现的导出符号名
 * @param args   原函数入参，仅用于占位
 */
export function notImplemented(symbol: string, ...args: readonly unknown[]): never {
  void args;
  throw new NotImplementedError(symbol);
}

/** 内部不变量被破坏（如实体守恒失败）。这类错误意味着引擎有 bug，不应被吞掉。 */
export class InvariantViolationError extends Error {
  override readonly name = 'InvariantViolationError';
  constructor(
    message: string,
    /** 便于日志定位的上下文，禁止放入任何会下发到客户端的载荷。 */
    readonly context?: Readonly<Record<string, unknown>>,
  ) {
    super(`[@majiang/core] 不变量被破坏: ${message}`);
  }
}

/** 规则配置非法（`normalizeRuleConfig` 校验未通过）。 */
export class RuleConfigError extends Error {
  override readonly name = 'RuleConfigError';
  constructor(
    message: string,
    /** 逐条问题明细。 */
    readonly issues: readonly { path: string; message: string }[],
  ) {
    super(`[@majiang/core] 规则配置非法: ${message}`);
  }
}

/**
 * 动作被拒绝的原因码。
 * 与 `action_rejected` 事件载荷一一对应；客户端据此做本地提示，不做业务判断。
 */
export type RejectReason =
  /** 当前阶段不接受该动作 */
  | 'wrong_phase'
  /** 不是你的回合 */
  | 'not_your_turn'
  /** 乐观并发校验失败：`expectedSeq` 与服务端状态不符 */
  | 'stale_seq'
  /** 该动作不在合法动作集中 */
  | 'illegal_action'
  /** 引用了不属于你或不存在的牌 */
  | 'tile_not_owned'
  /** 违反定缺约束（如仍有定缺花色却打了别的牌） */
  | 'void_suit_violation'
  /** 响应窗口已关闭 */
  | 'claim_window_closed'
  /** 该座位已胡牌，不再参与摸打 */
  | 'already_won'
  /** 客户端不得提交此类型动作（如 `timeout`） */
  | 'host_only_action';
